import request from '@/api/request'
import type { CommissionParticipant } from '@/api/commission-accrual'

export interface Account {
  id: number
  name: string
  type: 'cash' | 'bank' | 'wechat' | 'alipay' | 'credit'
  balance: number
  isActive: boolean
  /** 允许负余额（透支）：转账/付款/退款/报销/返利不受余额不足限制；信用卡恒为 true */
  allowNegative?: boolean
  cashierUserId?: number | null
  cashierName?: string | null
  cashierUsername?: string | null
  /** 收款手续费比例（小数，0.006=0.6%）；null/undefined=不启用自动计算 */
  receiveFeeRate?: number | string | null
  /** 付款手续费比例（小数）；null/undefined=不启用自动计算 */
  payFeeRate?: number | string | null
  createdAt?: string
}

export interface PaymentRecord {
  id: number
  paymentNo?: string
  type: 'receive' | 'pay' | 'transfer'
  /** 记录来源：payment=收付款记录，transfer=账户互转（仅查询勾选包含互转时出现） */
  recordType?: 'payment' | 'transfer'
  customerId?: number
  customerName?: string
  supplierId?: number
  supplierName?: string
  orderId?: number
  purchaseOrderId?: number
  accountId: number
  accountName?: string
  amount: number
  actualAmount: number
  fee?: number
  feeRate?: number
  /** 互转手续费归属（仅 recordType='transfer' 时有值）：from=转出方承担/to=转入方承担 */
  feePayer?: 'from' | 'to'
  discount?: number
  discountType?: string
  paymentMethod: 'cash' | 'transfer' | 'check' | 'wechat' | 'alipay' | 'offset'
  transactionNo?: string
  paymentDate: string
  remark?: string
  images?: string[]
  createdBy?: number
  createdByName?: string
  createdAt?: string
  groupNo?: string | null
  allocCount?: number
  orderNo?: string | null
  /** 来源单据 id（收款 sourceType='receivable' 时为销售单 id，分摊明细行可用于定位核销订单） */
  sourceId?: number
  sourceType?: string
  updatedBy?: number | null
  updatedByName?: string | null
  updatedAt?: string | null
  offsetCount?: number
}

export interface PaymentQuery {
  page?: number
  pageSize?: number
  type?: 'receive' | 'pay' | 'transfer' | 'discount_refund'
  accountId?: number
  customerId?: number
  supplierId?: number
  minAmount?: number
  maxAmount?: number
  remark?: string
  startDate?: string
  endDate?: string
  keyword?: string
  groupDetail?: string
  /** 是否在列表/汇总中包含账户互转记录 */
  includeTransfers?: boolean
  /** 导出模式（传 1）：需 finance:payment-export 权限，pageSize 上限放宽至 10000 供全量拉取 */
  export?: number
  /** 排序字段（后端白名单：paymentNo/type/accountName/customerName/supplierName/amount/actualAmount/fee/discount/paymentMethod/paymentDate/createdAt） */
  sortBy?: string
  /** 排序方向 */
  sortOrder?: 'asc' | 'desc'
}

export interface CreateAccountData {
  name: string
  /** 账户类型：cash现金/bank银行/wechat微信/alipay支付宝/credit信用卡（负债账户，允许负余额转账付款） */
  type: 'cash' | 'bank' | 'wechat' | 'alipay' | 'credit'
  balance?: number
  cashierUserId?: number | null
  /** 允许负余额（透支）；credit 类型后端强制为 1 */
  allowNegative?: boolean
  /** 收款手续费比例（小数，0.006=0.6%）；不传/null=不启用 */
  receiveFeeRate?: number | null
  /** 付款手续费比例（小数）；不传/null=不启用 */
  payFeeRate?: number | null
}

export interface TransferData {
  toAccountId: number
  amount: number
  fee?: number
  /** 手续费归属：from=转出方承担（默认）/to=转入方承担 */
  feePayer?: 'from' | 'to'
  remark?: string
  transferDate?: string
}

export interface PendingTransfer {
  id: number
  fromAccountId: number
  toAccountId: number
  amount: number
  fee: number
  feePayer?: 'from' | 'to'
  status: 'pending' | 'completed' | 'rejected'
  remark?: string
  transferDate?: string
  createdBy: number
  createdAt: string
  fromAccountName?: string
  toAccountName?: string
  fromCashierUserId?: number | null
  creatorName?: string
  creatorUsername?: string
  fromAccount?: { id: number; name: string }
  toAccount?: { id: number; name: string }
}

export interface CreatePaymentData {
  type: 'receive' | 'pay'
  customerId?: number
  supplierId?: number
  orderId?: number
  // 按客户总额收款：不指定单张订单，由后端按客户欠款订单从旧到新自动分摊核销（超出部分记预收）
  allocateByCustomer?: boolean
  // 付款：按供应商未付应付单从旧到新自动分摊核销（超出欠付部分记供应商预付）
  allocateBySupplier?: boolean
  // 付款：不核销应付单，全额记供应商预付（对账按供应商归属）
  noAllocate?: boolean
  // 手工指定核销明细（优先于 allocateByCustomer/allocateBySupplier/orderId）：
  // 收款 [{orderId, amount}]（合计≤收款金额，超出记预收）；付款 [{payableId, amount}]（合计须=付款金额）
  allocations?: Array<{ orderId?: number; payableId?: number; amount: number }>
  purchaseOrderId?: number
  accountId: number
  amount: number
  actualAmount: number
  fee?: number
  feeRate?: number
  discount?: number
  discountType?: string
  paymentMethod: 'cash' | 'transfer' | 'check' | 'wechat' | 'alipay'
  transactionNo?: string
  paymentDate: string
  remark?: string
  images?: string[]
  /** 按收款计提模式：参与员工及占比（ratio 百分比合计 100） */
  commissionParticipants?: CommissionParticipant[]
  /** 编辑收款分摊组时的重建策略：scale=保持原分摊结构按原比例缩放（默认）/fifo=按客户当前欠款重新自动分摊；传 allocations 手工明细时优先 */
  reallocate?: 'scale' | 'fifo'
}

// 查询资金账户列表（含余额；无 finance:account-all 权限仅返回本人管辖账户）
export function getAccountList() {
  return request.get('/accounts').then(res => res.data)
}

// 账户选项（轻量，不含余额，供报销单等业务下拉使用）
export interface AccountOption {
  id: number
  name: string
  type: 'cash' | 'bank' | 'wechat' | 'alipay' | 'credit'
  cashierUserId?: number | null
  cashierUsername?: string | null
  cashierName?: string | null
  /** 收款/付款手续费比例（小数，0.006=0.6%）；null/undefined=不启用自动计算 */
  receiveFeeRate?: number | string | null
  payFeeRate?: number | string | null
}
export function getAccountOptions() {
  return request.get('/accounts/options').then(res => res.data)
}

// 创建资金账户
export function createAccount(data: CreateAccountData) {
  return request.post('/accounts', data).then(res => res.data)
}

// 更新资金账户
export function updateAccount(id: number, data: Partial<CreateAccountData>) {
  return request.put(`/accounts/${id}`, data).then(res => res.data)
}

// 删除资金账户
export function deleteAccount(id: number) {
  return request.delete(`/accounts/${id}`).then(res => res.data)
}

// 账户互转（创建待审批单）
export function transferAccount(fromId: number, data: TransferData) {
  return request.post(`/accounts/${fromId}/transfer`, data).then(res => res.data)
}

// 待审批转账列表
export function getPendingTransfers() {
  return request.get('/accounts/transfers/pending').then(res => res.data)
}

// 审批转账
export function approveTransfer(id: number) {
  return request.post(`/accounts/transfers/${id}/approve`).then(res => res.data)
}

// 驳回转账
export function rejectTransfer(id: number, reason: string) {
  return request.post(`/accounts/transfers/${id}/reject`, { reason }).then(res => res.data)
}

// 编辑账户互转（finance:transfer-edit；pending 直接修改，completed 回滚余额后按新内容重新生效）
export function updateTransfer(id: number, data: Partial<TransferData>) {
  return request.put(`/accounts/transfers/${id}`, data).then(res => res.data)
}

// 删除账户互转（finance:transfer-delete；completed 回滚双方账户余额后删除）
export function deleteTransfer(id: number) {
  return request.delete(`/accounts/transfers/${id}`).then(res => res.data)
}

// 查询账户流水
export function getAccountTransactions(id: number) {
  return request.get(`/accounts/${id}/transactions`).then(res => res.data)
}

// 账户变更日志（余额/名称/类型等修改留痕）
export interface AccountChangeLog {
  id: number
  accountId: number
  accountName: string
  action: 'create' | 'update'
  changes: { field: string; label: string; oldValue: unknown; newValue: unknown }[]
  summary: string
  operatorId: number | null
  operatorName: string | null
  createdAt: string
}
export function getAccountLogs(id: number) {
  return request.get(`/accounts/${id}/logs`).then(res => res.data)
}

// 查询收付款记录
export function getPaymentList(params: PaymentQuery) {
  return request.get('/accounts/payments', { params }).then(res => res.data)
}

// 创建收付款记录
export function createPayment(data: CreatePaymentData) {
  return request.post('/accounts/payments', data).then(res => res.data)
}

// 客户预收（sourceId=0 挂客户级的收款余额）
export interface PrepaymentRow {
  id: number
  paymentNo: string
  amount: number
  actualAmount: number
  paymentMethod: string | null
  transactionNo: string | null
  remark: string | null
  groupNo: string | null
  paymentDate: string | null
  accountName: string | null
}

// 获取客户预收列表
export function getPrepayments(customerId: number) {
  return request.get('/accounts/payments/prepayments', { params: { customerId } }).then(res => res.data)
}

export interface PrepaymentAllocatePayload {
  customerId: number
  prepayments: Array<{ paymentId: number; amount: number }>
  orders: Array<{ orderId: number; amount: number }>
}

// 预收核销（拆分改挂：预收余额拆分到欠款订单，不动账户余额；需 finance:account-all）
export function allocatePrepayment(data: PrepaymentAllocatePayload) {
  return request.post('/accounts/payments/allocate-prepayment', data).then(res => res.data)
}

// 优惠退款（不退货纯折让真退钱；需 finance:account-all）：
// 后端生成同组双流水——付款流水扣账户余额 + 优惠核销流水不过账户，对账"已退"体现
export interface DiscountRefundPayload {
  customerId: number
  accountId: number
  amount: number
  paymentMethod: string
  paymentDate?: string
  remark?: string
  images?: string[]
}

// 查询客户优惠退款可退上限（净收款 - 已退款）
export function getDiscountRefundAvailable(customerId: number) {
  return request.get('/accounts/payments/discount-refund/available', { params: { customerId } }).then(res => res.data)
}

// 登记优惠退款
export function createDiscountRefund(data: DiscountRefundPayload) {
  return request.post('/accounts/payments/discount-refund', data).then(res => res.data)
}

// 撤销预收核销（拆分行删除退回预收/改挂行归回预收，不动账户余额；需 finance:account-all）
export function reverseAllocation(id: number) {
  return request.post(`/accounts/payments/${id}/reverse-allocation`).then(res => res.data)
}

// 查询收付款详情
export function getPaymentDetail(id: number) {
  return request.get(`/accounts/payments/${id}`).then(res => res.data)
}

// 修改收付款记录（单条/收款分摊组；收款分摊组整组重建，data 带 rebuilt/oldGroupNos/createdPaymentNo/paymentGroupNo/receiveAllocation，付款分摊组仍 400）
export function updatePayment(id: number, data: Partial<CreatePaymentData>) {
  return request.put(`/accounts/payments/${id}`, data).then(res => res.data)
}

// 收付款修改日志
export function getPaymentLogs(id: number) {
  return request.get(`/accounts/payments/${id}/logs`).then(res => res.data)
}

// 删除收付款记录
export function deletePayment(id: number) {
  return request.delete(`/accounts/payments/${id}`).then(res => res.data)
}

// ===== 收款提报审批流（销售提报收款 → 出纳确认到账 → 财务终审生效） =====
// 无 finance:account-all 的用户在租户开关开启时，POST /accounts/payments 不直接入账，
// 响应 data={ pendingApproval: true, requestId, requestNo }，由本模块走两级审批

export type PaymentRequestStatus = 'pending_cashier' | 'pending_finance' | 'approved' | 'rejected' | 'cancelled'

export interface PaymentRequestAllocation {
  orderId?: number
  payableId?: number
  orderNo?: string
  amount: number
}

export interface PaymentRequest {
  id: number
  requestNo: string
  customerId?: number | null
  customerName?: string | null
  orderId?: number | null
  accountId: number
  accountName?: string | null
  amount: number
  actualAmount: number
  fee?: number
  discount?: number
  paymentMethod: string
  paymentDate?: string | null
  transactionNo?: string | null
  remark?: string | null
  /** 列表行为 JSON 字符串或数组，详情接口已解析为数组 */
  images?: string[] | string | null
  allocateMode?: 'none' | 'customer' | 'manual' | null
  /** 列表行可能为 JSON 字符串，详情接口已解析 */
  allocationsJson?: string | PaymentRequestAllocation[] | null
  status: PaymentRequestStatus
  cashierReviewedBy?: number | null
  cashierReviewedAt?: string | null
  cashierRemark?: string | null
  financeReviewedBy?: number | null
  financeReviewedAt?: string | null
  rejectReason?: string | null
  createdBy?: number
  createdByName?: string | null
  employeeId?: number | null
  paymentGroupNo?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface PaymentRequestQuery {
  /** todo=待我审批 / mine=我的提报 / all=全部（finance:account-all） */
  scope?: 'todo' | 'mine' | 'all'
  status?: PaymentRequestStatus | ''
  page?: number
  pageSize?: number
}

// 提报审批开关（任何登录用户可读）：data={ enabled }
export function getPaymentRequestConfig() {
  return request.get('/payment-requests/config').then(res => res.data)
}

// 提报单列表：data={ list, pagination }
export function getPaymentRequestList(params: PaymentRequestQuery) {
  return request.get('/payment-requests', { params }).then(res => res.data)
}

// 提报单详情（allocations/images 已解析）
export function getPaymentRequestDetail(id: number) {
  return request.get(`/payment-requests/${id}`).then(res => res.data)
}

// 出纳到账确认（finance:payment-cashier-review）
export function cashierApprovePaymentRequest(id: number, remark?: string) {
  return request.post(`/payment-requests/${id}/cashier-approve`, { remark }).then(res => res.data)
}

// 出纳驳回（finance:payment-cashier-review，reason 必填）
export function cashierRejectPaymentRequest(id: number, reason: string) {
  return request.post(`/payment-requests/${id}/cashier-reject`, { reason }).then(res => res.data)
}

// 财务终审入账（finance:payment-finance-review）
export function financeApprovePaymentRequest(id: number, remark?: string) {
  return request.post(`/payment-requests/${id}/finance-approve`, { remark }).then(res => res.data)
}

// 财务驳回（finance:payment-finance-review，reason 必填）
export function financeRejectPaymentRequest(id: number, reason: string) {
  return request.post(`/payment-requests/${id}/finance-reject`, { reason }).then(res => res.data)
}

// 撤销提报（pending_cashier 且本人提报）
export function cancelPaymentRequest(id: number) {
  return request.post(`/payment-requests/${id}/cancel`).then(res => res.data)
}

// 删除提报单（仅 rejected/cancelled，进回收站可恢复）
export function deletePaymentRequest(id: number) {
  return request.delete(`/payment-requests/${id}`).then(res => res.data)
}

// 手续费汇总报表
export function getFeeReport(params?: { startDate?: string; endDate?: string; accountId?: number }) {
  return request.get('/accounts/fee-report', { params }).then(res => res.data)
}

// 优惠汇总报表
export function getDiscountReport(params?: { startDate?: string; endDate?: string }) {
  return request.get('/accounts/discount-report', { params }).then(res => res.data)
}
